import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  isValidAbaRoutingNumber,
  VENDOR_PAYMENT_METHOD_SPECS,
  VENDOR_PAYMENT_METHOD_TYPES,
  VendorPaymentMethodType,
  vendorPaymentMethodSchema,
  type PaymentFieldSpec,
  type VendorPaymentMethod,
  type VendorPaymentMethodInput,
} from '@peles/shared';
import { EntityModal } from '../../common/EntityModal';
import {
  GoogleAddressAutocomplete,
  type AddressFields,
} from '../../common/GoogleAddressAutocomplete';
import { SegmentedControl } from '../../common/SegmentedControl';
import { lookupRoutingNumber } from '../../../api/vendors';

export interface PaymentMethodFormModalProps {
  open: boolean;
  onClose: () => void;
  /** The method being edited; absent to add one. */
  method?: VendorPaymentMethod | null;
  onSave: (input: VendorPaymentMethodInput) => Promise<unknown>;
  saving?: boolean;
}

type Errors = Record<string, string>;

const inputClass =
  'w-full min-h-11 sm:min-h-0 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function detailsToValues(method?: VendorPaymentMethod | null): Record<string, string> {
  if (!method) return {};
  return Object.fromEntries(
    Object.entries(method.details as unknown as Record<string, unknown>)
      .filter(([, v]) => v != null)
      .map(([k, v]) => [k, String(v)]),
  );
}

/**
 * Adds or edits one of a vendor's payment methods. The fields are the type's
 * own, from `VENDOR_PAYMENT_METHOD_SPECS`, and are checked against the same
 * schema the server holds them to before anything is sent.
 */
export function PaymentMethodFormModal({
  open,
  onClose,
  method,
  onSave,
  saving = false,
}: PaymentMethodFormModalProps) {
  const [type, setType] = useState<VendorPaymentMethodType>(VendorPaymentMethodType.ZELLE);
  const [values, setValues] = useState<Record<string, string>>({});
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [isPreferred, setIsPreferred] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  /** What the last picked suggestion wrote into each address part. */
  const suggestedRef = useRef<Record<string, string>>({});
  /** The bank name the last lookup wrote — replaced by the next one, unlike a name typed by hand. */
  const lookedUpBankRef = useRef<string | null>(null);

  // Filled once as the dialog opens, never while it is open.
  useEffect(() => {
    if (!open) return;
    setType(method?.type ?? VendorPaymentMethodType.ZELLE);
    setValues(detailsToValues(method));
    suggestedRef.current = {};
    lookedUpBankRef.current = null;
    setLabel(method?.label ?? '');
    setNotes(method?.notes ?? '');
    setIsPreferred(method?.isPreferred ?? false);
    setErrors({});
    setSaveError(null);
  }, [open, method]);

  const spec = VENDOR_PAYMENT_METHOD_SPECS[type];

  const routing = type === VendorPaymentMethodType.WIRE_ACH ? (values.routingNumber ?? '') : '';
  const wireRouting =
    type === VendorPaymentMethodType.WIRE_ACH ? (values.wireRoutingNumber ?? '') : '';

  // A bank account's bank is named from its routing number (the ACH one first), never typed.
  useEffect(() => {
    if (!open) return;
    const candidates = [routing, wireRouting]
      .map((v) => v.replace(/[\s-]/g, ''))
      .filter(isValidAbaRoutingNumber);
    // A name a lookup wrote belongs to the number it was looked up for: with no number
    // the directory knows, it goes (a name typed by hand stays).
    const clearLookedUp = () =>
      setValues((v) => {
        const written = lookedUpBankRef.current;
        if (written === null || (v.bankName?.trim() ?? '') !== written) return v;
        lookedUpBankRef.current = null;
        return { ...v, bankName: '' };
      });
    // The numbers changed, so a name looked up for the old ones goes now — never left
    // standing beside the new number while its lookup is on the way (a Save then would keep it).
    clearLookedUp();
    if (candidates.length === 0) return;
    let stale = false;
    void (async () => {
      for (const candidate of candidates) {
        const found = await lookupRoutingNumber(candidate);
        if (stale) return;
        if (!found) continue;
        setValues((v) => {
          const current = v.bankName?.trim() ?? '';
          if (current && current !== lookedUpBankRef.current) return v;
          lookedUpBankRef.current = found.bankName;
          return { ...v, bankName: found.bankName };
        });
        setErrors((e) => {
          if (!e.bankName) return e;
          const next = { ...e };
          delete next.bankName;
          return next;
        });
        return;
      }
      clearLookedUp();
    })();
    return () => {
      stale = true;
    };
  }, [open, routing, wireRouting]);

  const setField = (field: PaymentFieldSpec, raw: string) => {
    setValues((v) => ({ ...v, [field.key]: raw }));
    setErrors((e) => {
      const next = { ...e };
      delete next[field.key];
      return next;
    });
  };

  /**
   * A picked Places suggestion: the street line, and the method's own
   * city/state/ZIP/country. A part the suggestion does not carry keeps what was
   * typed by hand, but not what an earlier suggestion wrote — that belonged to
   * the other address (a Miami ZIP must not stay beside Dublin).
   */
  const fillAddress = (field: PaymentFieldSpec, place: AddressFields) => {
    const fills: Record<string, string> = {};
    if (place.address) fills[field.key] = place.address;
    const keys = new Set(spec.fields.map((f) => f.key));
    const suggested: Record<string, string> = {};
    for (const key of ['city', 'state', 'zipCode', 'country'] as const) {
      if (!keys.has(key)) continue;
      if (place[key]) {
        // The country only when it is not the US — the field reads "If not the US".
        fills[key] =
          key === 'country' && /^(US|USA|United States)$/i.test(place[key]) ? '' : place[key];
        suggested[key] = fills[key];
      } else if (
        suggestedRef.current[key] !== undefined &&
        values[key] === suggestedRef.current[key]
      ) {
        fills[key] = '';
      }
    }
    suggestedRef.current = suggested;
    setValues((v) => ({ ...v, ...fills }));
    setErrors((e) => {
      const next = { ...e };
      for (const key of Object.keys(fills)) delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const details = Object.fromEntries(
      spec.fields.map((f) => [f.key, values[f.key]?.trim() ? values[f.key] : null]),
    );
    const parsed = vendorPaymentMethodSchema.safeParse({
      type,
      details,
      label,
      notes,
      isPreferred,
    });
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] === 'details' ? String(issue.path[1]) : String(issue.path[0]);
        next[key] ??= issue.message;
      }
      setErrors(next);
      return;
    }
    setSaveError(null);
    try {
      await onSave(parsed.data as VendorPaymentMethodInput);
      onClose();
    } catch {
      setSaveError('The payment method could not be saved. Check the details and try again.');
    }
  };

  const title = method ? `Edit ${spec.label}` : 'Add payment method';

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 sm:min-h-0 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="vendor-payment-method-form"
            disabled={saving}
            className="min-h-11 sm:min-h-0 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <form
        id="vendor-payment-method-form"
        onSubmit={(e) => void handleSubmit(e)}
        className="space-y-4"
        noValidate
      >
        {!method && (
          <SegmentedControl
            ariaLabel="Payment method type"
            variant="chips"
            value={type}
            onChange={(t) => {
              setType(t);
              setErrors({});
            }}
            options={VENDOR_PAYMENT_METHOD_TYPES.map((t) => ({
              id: t,
              label: VENDOR_PAYMENT_METHOD_SPECS[t].label,
            }))}
          />
        )}
        <p className="text-xs text-gray-500">{spec.description}</p>
        {spec.oneOf && (
          <p className="text-xs text-gray-500">
            Fill in at least one of:{' '}
            {spec.oneOf.map((k) => spec.fields.find((f) => f.key === k)?.label ?? k).join(' or ')}.
          </p>
        )}

        {spec.fields.map((field) => {
          const id = `pm-${field.key}`;
          const error = errors[field.key];
          return (
            <div key={`${type}-${field.key}`}>
              <label
                htmlFor={field.input === 'select' ? undefined : id}
                id={`${id}-label`}
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {field.label}
                {field.required && <span className="text-red-600"> *</span>}
              </label>
              {field.input === 'select' ? (
                // A choice of two or three, shown at once rather than behind a dropdown.
                <SegmentedControl
                  ariaLabel={field.label}
                  variant="chips"
                  value={values[field.key] ?? ''}
                  onChange={(v) => setField(field, v)}
                  options={(field.options ?? []).map((o) => ({ id: o.value, label: o.label }))}
                />
              ) : field.input === 'address' ? (
                <GoogleAddressAutocomplete
                  id={id}
                  value={values[field.key] ?? ''}
                  onChange={(v) => setField(field, v)}
                  onAddressSelect={(place) => fillAddress(field, place)}
                  placeholder={field.placeholder}
                  ariaInvalid={!!error}
                  ariaDescribedBy={error ? `${id}-error` : undefined}
                  className={`${inputClass} ${error ? 'border-red-400' : 'border-gray-300'}`}
                />
              ) : (
                <input
                  id={id}
                  type={field.input}
                  inputMode={field.digitsOnly ? 'numeric' : undefined}
                  autoComplete="off"
                  value={values[field.key] ?? ''}
                  onChange={(e) => setField(field, e.target.value)}
                  placeholder={field.placeholder}
                  aria-invalid={!!error}
                  aria-describedby={error ? `${id}-error` : undefined}
                  className={`${inputClass} ${error ? 'border-red-400' : 'border-gray-300'}`}
                />
              )}
              {error ? (
                <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
                  {error}
                </p>
              ) : (
                field.help && <p className="mt-1 text-xs text-gray-500">{field.help}</p>
              )}
            </div>
          );
        })}

        <div>
          <label htmlFor="pm-label" className="block text-sm font-medium text-gray-700 mb-1">
            Nickname
          </label>
          <input
            id="pm-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Optional — e.g. Deposits"
            className={`${inputClass} border-gray-300`}
          />
        </div>
        <div>
          <label htmlFor="pm-notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="pm-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Optional — e.g. put the invoice number in the memo"
            className={`${inputClass} border-gray-300 resize-none`}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 min-h-11 sm:min-h-0">
          <input
            type="checkbox"
            checked={isPreferred}
            onChange={(e) => setIsPreferred(e.target.checked)}
            className="h-4 w-4"
          />
          The vendor’s preferred way to be paid
        </label>
        {saveError && (
          <p role="alert" className="text-sm text-red-600">
            {saveError}
          </p>
        )}
      </form>
    </EntityModal>
  );
}
